import moment from "moment";
import PROOF_EVENTS from "./proofEvents";
import { EXPIRED_COOKIE_DATE, TYPES_ENUM, DOC_TYPES, PROOF_VERSION_STATUS, PROOF_STATUS_CLASS, PROOF_STATUS_TEXT } from "./constants";
import EnvService from "../env.settings"
import { ONBOARDING_TODO_ITEMS, LOCALE, TIME_SEPARATOR, ISO_SEPARATOR, ISO_MILLISECONDS, KEYDOWN } from "./constants";

const { AnnotationCommentThreadUpdated, ApproverAutoReminderQueued, ProofSendScheduled } = PROOF_EVENTS
const { STRING } = TYPES_ENUM;
const { VIDEO, AUDIO, IMAGE, DOCUMENT } = DOC_TYPES;
const { ENTER } = KEYDOWN;

export const getAppEventProperty = (event, field, defaultValue) => {
    return _.get(event, `appEventObject.${field}`, defaultValue);
}

// Watermarking is available on standard and premium plans,
// plus the legacy Business plans (enterprise-* SKUs).
export const isWatermarkPlanEnabled = (auth) => {
    const sku = _.get(auth, "account.subscriptionState.sku", "") || "";
    const tier = sku.split("-")[0];
    return tier === "standard" || tier === "premium" || tier === "enterprise";
}

// Proof password protection is available on standard and premium plans,
// plus the legacy Business plans (enterprise-* SKUs).
export const isProofPasswordPlanEnabled = (auth) => {
    const sku = _.get(auth, "account.subscriptionState.sku", "") || "";
    const tier = sku.split("-")[0];
    return tier === "standard" || tier === "premium" || tier === "enterprise";
}

export const hasCommentAnnotation = (event) => {
    return !!getAppEventProperty(event, 'commentAnnotation') || !!getAppEventProperty(event, 'additionalData.commentAnnotation');
}

export const sortEventsChronologically = (events) => {
    return events.sort((a, b) => {
      const getCreatedAt = (event) => {
        let eventCreatedAt = event.createdAt;
        if (event.eventType === AnnotationCommentThreadUpdated && hasCommentAnnotation(event)) 
          eventCreatedAt = getAppEventProperty(event, "proofAnnotation.createdAt") || getAppEventProperty(event, "additionalData.commentAnnotation.createdAt");

        const localDateTime = new Date(eventCreatedAt);
        const isUTC = typeof eventCreatedAt === STRING && eventCreatedAt.endsWith('Z');

        const timezoneOffset = localDateTime.getTimezoneOffset() * 60000;
        const adjustedDateTime = isUTC ? localDateTime : new Date(localDateTime.getTime() - timezoneOffset);

        return adjustedDateTime;
      };

      return getCreatedAt(b) - getCreatedAt(a);
    });
}

const { webbApp } = EnvService.instance;
export const isCustomDomain = window.location.hostname !== new URL(webbApp).hostname && window.location.hostname !== "localhost";

let baseTitle = "";
let pageTitle = "";

const applyDocumentTitle = () => {
  document.title = [pageTitle, baseTitle].filter(Boolean).join(" · ") || "\u200b";
};

// Sets the page-specific part of the browser title (e.g. "Proof Name (#123)").
// Pass an empty string to clear it and fall back to the account title alone.
export const setPageTitle = (title = "") => {
  pageTitle = title;
  applyDocumentTitle();
};

export const updateFaviconAndTitle = ({ accountLogo = "", accountTitle = "" }) => {
  try {
    if (accountTitle) {
      baseTitle = `${accountTitle} App`;
      applyDocumentTitle();
    }

    if (accountLogo) {
      localStorage.setItem(
        `accountLogo${window.location.hostname}`,
        accountLogo
      );
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "shortcut icon";
        link.type = "image/x-icon";
        document.head.appendChild(link);
      }
      link.href = accountLogo;
    }
  } catch (error) {
    console.error("Error updating Meta Details:", error);
  }
};

export const getAccountLogo = () => {
  const logo = localStorage.getItem(`accountLogo${window.location.hostname}`) || `${window.location.origin}/img/logo-large.svg`;
  // Older sessions stored the default logo as a relative path ("img/..."),
  // which breaks on nested routes like /sign-up/appsumo
  return logo.startsWith("img/") ? `/${logo}` : logo;
}

export const isEligibleForFirstTimeOffer = (auth, isFreeUser = true) => {
  if(!isFreeUser) return false;
  const onboardedAt = auth?.onboardedAt;
  if (!onboardedAt || auth?.onboardingStep === 1) return false;

  const twoHoursAfterOnboarding = new Date(onboardedAt + 'Z');
  twoHoursAfterOnboarding.setHours(twoHoursAfterOnboarding.getHours() + 2);

  return new Date() <= twoHoursAfterOnboarding;
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

export const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie =
    name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=${EXPIRED_COOKIE_DATE}; path=/; SameSite=Lax`;
};

// A thumbnail is still on its way only while a conversion job is in flight.
// Jobs that silently die would read as "generating" forever, so cap the wait.
// The API serializes unset dates as 0001-01-01 (truthy in JS), hence the year check.
export const proofFilePreviewPending = (file) => {
  const isSetDate = (d) => d && moment(d).year() > 1971;
  return (
    !file.thumbnailFileKey &&
    isSetDate(file.conversionJobQueuedAt) &&
    !isSetDate(file.conversionJobCompletedAt) &&
    moment(file.conversionJobQueuedAt).isAfter(moment().subtract(30, "minutes"))
  );
};

export const getDefaultThumbnail = (fileType) => {
  switch (fileType) {
    case VIDEO:
      return "/img/video-thumbnail.png";
    case AUDIO:
      return "/img/audio-thumbnail.svg";
    case IMAGE:
      return "/img/image-thumbnail.png";
    case DOCUMENT:
      return "/img/pdf-thumbnail.png";
    default:
      return "";
  }
};

export function deduplicateApproverReminders(events) {
  const seenEmails = new Set();
  return events.filter((s) => {
    if (s.eventType === ApproverAutoReminderQueued) {
      const emailMatch = s.description.match(/<([^>]+)>/);
      if (!emailMatch) {
        return !seenEmails.has(s.id);
      }
      
      const email = emailMatch[1];
      if (seenEmails.has(email)) {
        return false;
      } else {
        seenEmails.add(email);
        return true;
      }
    }
    return true;
  });
}

export function sortApproverRemindersFirst(events) {
  return events.sort((a, b) => {
    const queuedTypes = [ApproverAutoReminderQueued, ProofSendScheduled];
    const isAQueued = queuedTypes.indexOf(a.eventType) !== -1;
    const isBQueued = queuedTypes.indexOf(b.eventType) !== -1;
    if (isAQueued && !isBQueued) return -1;
    if (!isAQueued && isBQueued) return 1;
    if (isAQueued && isBQueued) {
      return new Date(b.queuedActionAt) - new Date(a.queuedActionAt);
    }
    return 0;
  })
}

export const getPendingTodoItems = (onboardingState) => {
  if (!onboardingState?.todoState || !onboardingState?.modelState) return [];

  const hasUnseenModals = Object.values(onboardingState.modelState).includes(false);
  if (!hasUnseenModals) return [];

  const todoMappings = {
    hasCreatedProof: ONBOARDING_TODO_ITEMS.CREATE_PROOF,
    hasReviewLinkRegistered: ONBOARDING_TODO_ITEMS.SHARE_REVIEW_LINK,
    hasEmailTemplateCreated: ONBOARDING_TODO_ITEMS.CREATE_EMAIL_TEMPLATE,
    hasUsedWorkflow: ONBOARDING_TODO_ITEMS.SEND_WORKFLOW,
  };

  const todoState = onboardingState.todoState;

  return Object.entries(todoMappings)
    .filter(([key]) => !todoState[key])
    .map(([, item]) => item);
};

export const getCurrentDateString = () => {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};

export const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

export const handleDropdownPosition = () => {
  try {
    const dropdowns = document.querySelectorAll(".dropdown-wrapper-js");
    Array.from(dropdowns).forEach((dropdown) => {
      const dropdownButton = dropdown.querySelector(".dropdown-toggle-js");
      if (!dropdownButton) return;

      // Avoid attaching duplicate listeners
      if (dropdownButton.getAttribute("data-dd-init") === "1") return;
      dropdownButton.setAttribute("data-dd-init", "1");

      dropdownButton.addEventListener("click", () => {
        const dropdownSelector =
          dropdown.getAttribute("data-dropdown-selector") || ".dropdown-js";

        const dropdownMenu = dropdown.querySelector(dropdownSelector);
        const dropdownWidth = dropdownMenu?.offsetWidth;

        // if (!dropdownMenu) return;
        const dropdownHeight = dropdownMenu?.offsetHeight;
        const buttonRect = dropdownButton.getBoundingClientRect();
        const buttonWidth = buttonRect.width;
        const buttonLeft = buttonRect.left;
        const buttonTop = buttonRect.top;
        const buttonBottom = buttonRect.bottom;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // --- Horizontal positioning ---
        const currentDropdownLeft = buttonWidth + buttonLeft - dropdownWidth;

        if (currentDropdownLeft < 0) {
          dropdownMenu?.style.setProperty(
            "right",
            currentDropdownLeft + "px",
            "important"
          );
        } else {
          // Reset custom positioning if not needed anymore
          dropdownMenu?.style.removeProperty("right");
        }

        // --- Vertical positioning ---
        const spaceBelow = viewportHeight - buttonBottom;
        const spaceAbove = buttonTop;

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          // Open upwards
          dropdownMenu?.style.setProperty("top", "auto", "important");
          dropdownMenu?.style.setProperty(
            "bottom",
            buttonRect.height + "px",
            "important"
          );
        } else {
          // Default: open downwards
          dropdownMenu?.style.setProperty("bottom", "auto", "important");
          dropdownMenu?.style.setProperty(
            "top",
            buttonRect.height + "px",
            "important"
          );
        }
      });
    });
  } catch (e) {
    // Fail-safe: do nothing
    console.error("Dropdown position error:", e);
  }
};

export function formatDateTime(date) {
    const currentDate = new Date(date);
    const dateOptions = { year: 'numeric', month: 'long', day: '2-digit' };
    const datePart = currentDate.toLocaleDateString('en-US', dateOptions);
    const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    };
    const timePart = currentDate.toLocaleTimeString('en-US', timeOptions);
    return `${datePart} ${timePart}`;
}

export function formatLocalDate(dateStr) {
  const localizedDate = new Date(dateStr).toLocaleString(LOCALE, { hour12: false });
  const isoLikeString = localizedDate.replace(TIME_SEPARATOR, ISO_SEPARATOR) + ISO_MILLISECONDS;
  const finalIsoDate = new Date(isoLikeString).toISOString();
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date(finalIsoDate))
}

export function formatDate(date) {
  if (!date) return "";
  const dateObj = new Date(date);
  const options = { month: "short", day: "2-digit", year: "numeric" };
  return dateObj.toLocaleDateString("en-US", options);
}

export function formatTagDisplayText(tagNames) {
  if (!tagNames || tagNames.length === 0) return '';
  if (tagNames.length <= 3) return tagNames.join(', ');
  return tagNames.slice(0, 3).join(', ') + '...';
}

// Assignee user ids for a job; falls back to the legacy single `assignee` field.
export function getJobAssigneeIds(job) {
  if (!job) return [];
  if (Array.isArray(job.assignees)) return job.assignees;
  return job.assignee ? [job.assignee] : [];
}

export function createTaggableUsers(currentUser, accountUsers = []) {
  let taggableUsers = [];
  const curUserId = _.get(currentUser, 'id');
  
  // Add current user if available
  if (currentUser) {
    taggableUsers.push({
      type: 'user',
      name: currentUser.name,
      email: currentUser.email,
      id: currentUser.id,
      initials: currentUser.initials || currentUser.name?.substring(0, 2).toUpperCase(),
      publicId: currentUser.publicId
    });
  }

  if (accountUsers && accountUsers.length > 0) {
    accountUsers.forEach(user => {
      if (user.id !== curUserId) {
        taggableUsers.push({
          type: 'user',
          name: user.name,
          email: user.email,
          id: user.id,
          initials: user.initials || user.name?.substring(0, 2).toUpperCase(),
          publicId: user.publicId
        });
      }
    });
  }
  
  return taggableUsers;
}

export function getProofStatusText(status) {
  const { APPROVED, APPROVED_WITH_CHANGES, NOT_APPROVED, CREATED, SENT, WAITING } = PROOF_VERSION_STATUS;
  const { APPROVED: APPROVED_TEXT, APPROVED_WITH_CHANGES: APPROVED_WITH_CHANGES_TEXT, NOT_APPROVED: NOT_APPROVED_TEXT, WAITING_FOR_REVIEW } = PROOF_STATUS_TEXT;
  
  switch (status) {
    case APPROVED:
      return APPROVED_TEXT;
    case APPROVED_WITH_CHANGES:
      return APPROVED_WITH_CHANGES_TEXT;
    case NOT_APPROVED:
      return NOT_APPROVED_TEXT;
    case CREATED:
    case SENT:
    case WAITING:
      return WAITING_FOR_REVIEW;
    default:
      return 'Unknown Status';
  }
}

export function getProofStatusColor(status) {
  const { APPROVED, APPROVED_WITH_CHANGES, NOT_APPROVED, CREATED, SENT, WAITING } = PROOF_VERSION_STATUS;
  const { APPROVED: APPROVED_CLASS, NOT_APPROVED: NOT_APPROVED_CLASS, WAITING_FOR_REVIEW } = PROOF_STATUS_CLASS;
  
  switch (status) {
    case APPROVED:
    case APPROVED_WITH_CHANGES:
      return APPROVED_CLASS;
    case NOT_APPROVED:
      return NOT_APPROVED_CLASS;
    case CREATED:
    case SENT:
    case WAITING:
      return WAITING_FOR_REVIEW;
    default:
      return WAITING_FOR_REVIEW;
  }
}

export function initProofStatusColor(proof, proofStatusColors) {
  const { APPROVED, APPROVED_WITH_CHANGES, NOT_APPROVED, CREATED, SENT, WAITING } = PROOF_VERSION_STATUS;
  const { APPROVED: APPROVED_TEXT, APPROVED_WITH_CHANGES: APPROVED_WITH_CHANGES_TEXT, NOT_APPROVED: NOT_APPROVED_TEXT, WAITING_FOR_REVIEW } = PROOF_STATUS_TEXT;
  const { APPROVED: APPROVED_CLASS, NOT_APPROVED: NOT_APPROVED_CLASS, WAITING_FOR_REVIEW: WAITING_FOR_REVIEW_CLASS } = PROOF_STATUS_CLASS;
  
  proofStatusColors[proof.id] = {};
  
  switch (proof.status) {
    case APPROVED:
      proofStatusColors[proof.id].status = APPROVED_TEXT;
      proofStatusColors[proof.id].color = APPROVED_CLASS;
      break;
    case APPROVED_WITH_CHANGES:
      proofStatusColors[proof.id].status = APPROVED_WITH_CHANGES_TEXT;
      proofStatusColors[proof.id].color = APPROVED_CLASS;
      break;
    case NOT_APPROVED:
      proofStatusColors[proof.id].status = NOT_APPROVED_TEXT;
      proofStatusColors[proof.id].color = NOT_APPROVED_CLASS;
      break;
    case CREATED:
    case SENT:
    case WAITING:
      proofStatusColors[proof.id].status = WAITING_FOR_REVIEW;
      proofStatusColors[proof.id].color = WAITING_FOR_REVIEW_CLASS;
      break;
  }
}

export function getProofStatusClass(proof, proofStatusColors) {
  return proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : getProofStatusColor(proof.status);
}

export function getProofStatusTextDisplay(proof, proofStatusColors) {
  return proofStatusColors[proof.id] ? proofStatusColors[proof.id].status : getProofStatusText(proof.status);
}

export function getVersionInfo(proof, formatLocalDate) {
  return `Version ${proof.currentVersion} Created ${formatLocalDate(proof.currentVersionCreatedAt)}`;
}

export async function createTagOnEnter(
  event,
  searchQuery,
  jobTags,
  jobTagService,
  clearSearch,
  showError
) {
  if (event.key !== ENTER) return;

  event.preventDefault();
  const tagName = searchQuery?.trim();
  if (!tagName) return;

  if (jobTags.some((tag) => tag.name.toLowerCase() === tagName.toLowerCase()))
    return;

  try {
    const newTag = await jobTagService.Save({ name: tagName });
    jobTags.push(newTag);
    clearSearch();
  } catch (error) {
    console.error("Failed to create tag:", error);
    showError("Failed to create tag");
  }
}

export const addTargetBlankToLinks = (content) => {
  if (!content) return content;
  return content.replace(/<a\s+([^>]*?)>/gi, function(match, attributes) {
    if (attributes.includes('target=')) {
      return match;
    }

    return '<a ' + attributes + ' target="_blank">';
  });
};
